/**
 * PIV Slot Status Detection Utility
 *
 * Provides functions to detect and analyze the status of PIV slots,
 * including checking for existing keys and certificates.
 */

import * as forge from 'node-forge';

/**
 * PIV slot ID to data object mapping
 */
const PIV_SLOT_MAP = {
  '9A': '05', // PIV Authentication
  '9C': '0A', // Digital Signature
  '9D': '0B', // Key Management
  '9E': '0C'  // Card Authentication
};

/**
 * PIV slot names and descriptions
 */
export const PIV_SLOTS = [
  { id: '9A', name: 'PIV Authentication', description: 'Used for authentication to systems' },
  { id: '9C', name: 'Digital Signature', description: 'Used for digital signatures' },
  { id: '9D', name: 'Key Management', description: 'Used for encryption and key management' },
  { id: '9E', name: 'Card Authentication', description: 'Used for card-based authentication' }
];

/**
 * Check the status of a PIV slot
 * @param {string} slotId - Slot ID ('9A', '9C', '9D', '9E')
 * @param {Function} transceive - Function to send APDU commands
 * @returns {Promise<Object>} Slot status information
 */
export async function checkPIVSlotStatus(slotId, transceive) {
  const slotDataObject = PIV_SLOT_MAP[slotId];

  if (!slotDataObject) {
    throw new Error(`Invalid PIV slot ID: ${slotId}`);
  }

  try {
    // 1. Check if certificate exists
    const certAPDU = `00CB3FFF055C035FC1${slotDataObject}`;
    const certResponse = await transceive(certAPDU);

    // 2. Check if private key exists (by reading public key)
    const pubKeyAPDU = `00CB3FFF055C035FC2${slotDataObject}`;
    const pubKeyResponse = await transceive(pubKeyAPDU);

    const hasCert = certResponse.endsWith('9000') && certResponse.length > 8;
    const hasPrivateKey = pubKeyResponse.endsWith('9000') && pubKeyResponse.length > 8;

    // Determine slot status
    let status = 'empty';
    if (hasCert && hasPrivateKey) {
      status = 'complete'; // Has both certificate and private key
    } else if (hasPrivateKey) {
      status = 'key_only'; // Only has private key, no certificate
    } else if (hasCert) {
      status = 'cert_only'; // Only has certificate, no private key (abnormal)
    }

    // 3. Parse certificate information if available
    let certInfo = null;
    if (hasCert) {
      try {
        certInfo = await parsePIVCertificate(certResponse);
      } catch (error) {
        console.error(`Failed to parse certificate for slot ${slotId}:`, error);
        certInfo = { error: error.message };
      }
    }

    return {
      slotId,
      status,
      hasCert,
      hasPrivateKey,
      certInfo,
      lastChecked: new Date().toISOString()
    };

  } catch (error) {
    console.error(`Failed to check PIV slot ${slotId}:`, error);
    return {
      slotId,
      status: 'error',
      error: error.message,
      lastChecked: new Date().toISOString()
    };
  }
}

/**
 * Parse PIV certificate information
 * @param {string} certResponse - APDU response containing certificate data
 * @returns {Object} Parsed certificate information
 */
async function parsePIVCertificate(certResponse) {
  try {
    // Remove status code (last 4 characters)
    const certHex = certResponse.slice(0, -4);

    // Parse TLV structure to extract certificate
    const certData = extractCertificateFromTLV(certHex);

    if (!certData) {
      throw new Error('Failed to extract certificate from TLV structure');
    }

    // Convert hex to binary
    const certBinary = forge.util.hexToBytes(certData);

    // Parse X.509 certificate
    const asn1 = forge.asn1.fromDer(certBinary);
    const cert = forge.pki.certificateFromAsn1(asn1);

    // Extract certificate information
    const subject = cert.subject.getField('CN')?.value ||
                   cert.subject.getField('O')?.value ||
                   'Unknown';
    const issuer = cert.issuer.getField('CN')?.value ||
                  cert.issuer.getField('O')?.value ||
                  'Unknown';

    // Determine key algorithm and size
    let algorithm = 'Unknown';
    let keySize = 'Unknown';

    if (cert.publicKey.n) {
      // RSA key
      algorithm = 'RSA';
      keySize = cert.publicKey.n.bitLength();
    } else if (cert.publicKey.curve) {
      // ECC key
      algorithm = 'ECC';
      keySize = cert.publicKey.curve;
    }

    // Calculate fingerprint
    const fingerprint = forge.md.sha256.create()
      .update(certBinary)
      .digest()
      .toHex()
      .toUpperCase();

    return {
      subject,
      issuer,
      validFrom: cert.validity.notBefore,
      validTo: cert.validity.notAfter,
      fingerprint,
      keySize,
      algorithm,
      serialNumber: cert.serialNumber
    };

  } catch (error) {
    console.error('Failed to parse certificate:', error);
    throw new Error(`Certificate parsing failed: ${error.message}`);
  }
}

/**
 * Extract certificate data from PIV TLV structure
 * @param {string} tlvHex - TLV encoded data in hex
 * @returns {string|null} Certificate data in hex, or null if not found
 */
function extractCertificateFromTLV(tlvHex) {
  try {
    // PIV certificate is typically wrapped in tag 53 (Certificate)
    // and tag 70 (Certificate Data)

    let pos = 0;
    const data = tlvHex;

    while (pos < data.length) {
      const tag = data.substr(pos, 2);
      pos += 2;

      if (pos >= data.length) break;

      // Parse length (simple implementation, assumes length < 128 or uses 0x82 format)
      let length;
      const firstLengthByte = parseInt(data.substr(pos, 2), 16);

      if (firstLengthByte < 0x80) {
        length = firstLengthByte;
        pos += 2;
      } else if (firstLengthByte === 0x81) {
        length = parseInt(data.substr(pos + 2, 2), 16);
        pos += 4;
      } else if (firstLengthByte === 0x82) {
        length = parseInt(data.substr(pos + 2, 4), 16);
        pos += 6;
      } else {
        // Unsupported length encoding
        break;
      }

      const value = data.substr(pos, length * 2);

      // Tag 53 or 70 typically contains the certificate
      if (tag === '53' || tag === '70') {
        // Recursively search within this tag
        const innerCert = extractCertificateFromTLV(value);
        if (innerCert) return innerCert;

        // Or this might be the certificate itself
        // X.509 certificates start with 0x30 (SEQUENCE)
        if (value.startsWith('30')) {
          return value;
        }
      }

      pos += length * 2;
    }

    // If no tag found, check if the entire data is a certificate
    if (tlvHex.startsWith('30')) {
      return tlvHex;
    }

    return null;
  } catch (error) {
    console.error('TLV parsing error:', error);
    return null;
  }
}

/**
 * Check all PIV slots in parallel
 * @param {Function} transceive - Function to send APDU commands
 * @returns {Promise<Array>} Array of slot status objects
 */
export async function checkAllPIVSlots(transceive) {
  const slotChecks = PIV_SLOTS.map(slot =>
    checkPIVSlotStatus(slot.id, transceive)
  );

  return await Promise.all(slotChecks);
}

/**
 * Get statistics about PIV slot statuses
 * @param {Array} slotStatuses - Array of slot status objects
 * @returns {Object} Statistics summary
 */
export function getPIVSlotStatistics(slotStatuses) {
  return {
    total: slotStatuses.length,
    empty: slotStatuses.filter(s => s.status === 'empty').length,
    complete: slotStatuses.filter(s => s.status === 'complete').length,
    keyOnly: slotStatuses.filter(s => s.status === 'key_only').length,
    certOnly: slotStatuses.filter(s => s.status === 'cert_only').length,
    errors: slotStatuses.filter(s => s.status === 'error').length
  };
}

/**
 * Get human-readable status label
 * @param {string} status - Status code
 * @returns {string} Human-readable label
 */
export function getStatusLabel(status) {
  const labels = {
    'empty': 'Empty',
    'complete': 'Configured',
    'key_only': 'Key Only (No Certificate)',
    'cert_only': 'Certificate Only (No Key)',
    'error': 'Error'
  };

  return labels[status] || 'Unknown';
}

/**
 * Get recommended action based on slot status
 * @param {string} status - Slot status
 * @returns {string} Recommended action
 */
export function getRecommendedAction(status) {
  const recommendations = {
    'empty': 'generate',
    'complete': 'skip',
    'key_only': 'generate_cert',
    'cert_only': 'generate',
    'error': 'skip'
  };

  return recommendations[status] || 'skip';
}
